// Session replay is a SEPARATE pipeline from ordinary event capture (rrweb
// DOM snapshots, mutations, and — if enabled — network request/response
// capture, shipped directly rather than through `before_send`/
// `sanitize_properties`). posthogSanitize.ts's `before_send` hook does not
// touch it at all. A human confirmed on 2026-09-22 that session replay IS
// enabled for this project — this is not a hypothetical gap anymore.
//
// Three independent layers here, in order of how much they can be trusted:
//
// Layer 1 (this file): don't record on /identity/* pages at all. This is
// the guarantee, because it covers the whole pipeline — DOM, mutations,
// network — not just a URL field. It needs TWO hooks, not one:
//   - `shouldDisableSessionRecordingAtInit`, read by posthog.ts and passed
//     as `disable_session_recording` to `posthog.init`. Stytch redirects the
//     browser DIRECTLY to /identity/callback, so arriving there on a fresh
//     page load is the COMMON case, not an edge case — and `posthog.init`
//     runs at module-eval time, before vue-router (or Vue itself) exists. A
//     router hook alone would always be one page load too late for that.
//   - `installIdentitySessionRecordingGuard`, for IN-APP navigation after
//     init has already run once and can't be re-run: stops recording on the
//     way into an identity route, restarts it on the way out.
// Both reuse IDENTITY_PATH_PREFIX from posthogSanitize.ts — the same
// definition of "identity route" as the URL sanitizer, not a second one.
//
// Layer 2 (wired in posthog.ts): `mask_personal_data_properties` +
// `custom_personal_data_properties`. Best-effort, not a guarantee — see the
// comment in posthog.ts for why.
//
// Layer 3 (this file): `maskIdentityNetworkRequest`, wired into posthog.ts
// as `session_recording.maskCapturedNetworkRequestFn`. Redacts the one
// network request whose body could carry a live credential regardless of
// which layer above holds.

import type { Router } from "vue-router"
import type { CapturedNetworkRequest } from "posthog-js"
import posthog from "posthog-js"
import { IDENTITY_PATH_PREFIX } from "@/init/posthogSanitize"

function isIdentityPath(pathname: string): boolean {
  return pathname.startsWith(IDENTITY_PATH_PREFIX)
}

/**
 * Decides the `disable_session_recording` value to pass into `posthog.init`,
 * from wherever the browser is loading RIGHT NOW. See the file header for
 * why this can't be a router hook alone. Never throws: `window.location`
 * always exists in this SPA's runtime (no SSR), but the pathname is passed
 * as a parameter rather than read internally so the function itself has no
 * hidden global dependency and is trivial to test.
 */
export function shouldDisableSessionRecordingAtInit(pathname: string): boolean {
  return isIdentityPath(pathname)
}

/**
 * Layer 1's other half. Registers a `router.beforeEach` hook that stops
 * recording on the way into an identity route and restarts it on the way
 * out, for in-app navigation (posthog.init already ran once by the time any
 * navigation happens, so it can't re-decide `disable_session_recording`).
 *
 * `enabled` mirrors whether `posthog.init` actually ran (posthog.ts skips it
 * in local dev / when no API key is configured) — when it didn't, this
 * function registers NO hook at all, rather than registering one that
 * silently no-ops on every navigation. That is a stronger guarantee than
 * relying on posthog-js's own pre-init behavior for `stopSessionRecording`/
 * `startSessionRecording`, and it means zero overhead in dev mode: no hook
 * runs, so there's nothing to reason about.
 *
 * The hook body is still wrapped in try/catch, and returns nothing, so
 * navigation always continues: a failure toggling session recording must
 * never block or throw into navigation. `before_send` runs on every capture and matters
 * everywhere; this matters everywhere navigation happens, which is just as
 * often, so the same "never break the page" standard applies.
 *
 * On splitting a recording: `stopSessionRecording`/`startSessionRecording`
 * only flip a local `disable_session_recording` flag (and, on restart,
 * re-check the existing session/window id) — they do not rotate the
 * analytics session id themselves. A detour through /identity/* pages
 * (typically seconds, well under the default 30-minute session idle
 * timeout) resumes recording under the SAME session in PostHog's UI, with a
 * gap where the identity pages were, not a second separate recording. An
 * identity detour that somehow took longer than the idle timeout would
 * start a new session regardless of these calls — that's the SDK's normal
 * idle-timeout behavior, not something this guard introduces or changes.
 */
export function installIdentitySessionRecordingGuard(router: Router, enabled: boolean): void {
  if (!enabled) return
  router.beforeEach((to, from) => {
    try {
      const toIdentity = isIdentityPath(to.path)
      const fromIdentity = isIdentityPath(from.path)
      if (toIdentity && !fromIdentity) {
        posthog.stopSessionRecording()
      } else if (!toIdentity && fromIdentity) {
        posthog.startSessionRecording()
      }
    } catch (e) {
      console.error("Heavybit Identity: could not toggle session recording", e)
    }
  })
}

// The one request whose body could carry a LIVE Stytch credential if
// network capture were ever on: the discovery-token exchange. Its POST body
// carries the single-use discovery token
// (auger-web/src/identity/stytch.ts::exchangeDiscoveryToken); its RESPONSE
// body carries something worse — session_token and session_jwt, which
// outlive the single-use token they're exchanged for (ExchangeResult, same
// file). Scoped to this one path on purpose, same as the URL sanitizer is
// scoped to /identity/* — this is not a general network-capture policy.
const AUTH_SESSION_PATH = "/.netlify/functions/auth-session"
const REDACTED = "[redacted: identity exchange]"

/**
 * `session_recording.maskCapturedNetworkRequestFn`. Redacts request/response
 * bodies for the identity token exchange; every other captured network
 * request passes through completely untouched. Never throws — a malformed
 * entry, a relative/unparseable `name`, or any other surprise degrades to
 * "leave this request alone" rather than breaking the recorder.
 *
 * Under this repo's current config this is inert: `session_recording`
 * carries no `recordBody`/`recordHeaders` override, both default to `false`
 * in the installed posthog-js version (@posthog/types 1.398.0), and this
 * repo never sets `defaults` to a preset that changes that default — so
 * network bodies aren't captured at all today, and there's nothing for this
 * function to redact. It's added anyway: Layer 1 above already stops this
 * exact request from ever being recorded under normal operation, so this
 * layer only matters if Layer 1 doesn't — and whether replay is even
 * enabled at all was itself a fact this repo couldn't confirm until a human
 * checked the dashboard, so "inert under today's config" is not the same as
 * "will always be inert." The cost of covering it is a few lines; the thing
 * it would catch, if it ever fires, is a live session credential.
 */
export function maskIdentityNetworkRequest(
  request: CapturedNetworkRequest
): CapturedNetworkRequest | null | undefined {
  try {
    if (!request || typeof request.name !== "string") return request
    let pathname: string
    try {
      pathname = new URL(request.name, window.location.origin).pathname
    } catch {
      return request
    }
    if (pathname !== AUTH_SESSION_PATH) return request
    return {
      ...request,
      requestBody: request.requestBody != null ? REDACTED : request.requestBody,
      responseBody: request.responseBody != null ? REDACTED : request.responseBody,
    }
  } catch {
    return request
  }
}
